import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { truncate } from "lodash";

import { Box } from "@/Components/v2/atoms/Box";
import { OutlinedButton } from "@/Components/v2/atoms/Button/OutlinedButton";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Collapse } from "@/Components/v2/atoms/Transitions/Collapse";
import { Typography } from "@/Components/v2/atoms/Typography";
import { LegendItem } from "@/Components/v2/molecule/LegendItem/LegendItem";
import { LegendItem as LegendItemType } from "@/hooks/shared/useLegends";

import { useFilterActions } from "../hooks/useFilterActions";

export type FilterSection = {
  id: string;
  title: string;
  items: LegendItemType[];
};

export type FilterComponentProps = {
  sections: FilterSection[];
};

const FilterComponent = ({ sections }: FilterComponentProps) => {
  const {
    activeFilterIds,
    expandedSectionIds,
    toggleSection,
    toggleItem,
    resetFilters,
    showOnlySection,
    hideAllSection,
  } = useFilterActions();

  if (!sections?.length) return null;

  return (
    <Box>
      <Stack justifyContent="flex-end" direction="row" mx={1.5}>
        <OutlinedButton
          size="small"
          onClick={() =>
            resetFilters(
              sections.map((s) => s.id),
              sections.flatMap((s) => s.items.map((i) => i.id))
            )
          }
          sx={{
            fontSize: 12,
            px: 0.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          Reset Filters
        </OutlinedButton>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="column" gap={2}>
        {sections.map((section, index) => (
          <Box key={section.id}>
            <Stack direction="column" gap={1.5} mx={1.5}>
              <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                <Typography variant="body1" fontSize={14} fontWeight={600}>
                  {truncate(section.title, { length: 18 })}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <OutlinedButton
                    size="small"
                    sx={{
                      fontSize: 12,
                      px: 1,
                      minWidth: 0,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                    onClick={() => {
                      showOnlySection(
                        section.id,
                        section.items.map((i) => i.id)
                      );
                    }}
                  >
                    Show Only
                  </OutlinedButton>
                  {(() => {
                    const allDisabled = section.items.every(
                      (item) => !activeFilterIds.includes(item.id)
                    );
                    return (
                      <OutlinedButton
                        size="small"
                        sx={{
                          fontSize: 12,
                          px: 1,
                          minWidth: 0,
                          height: 28,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                        onClick={() =>
                          hideAllSection(
                            section.id,
                            section.items.map((i) => i.id),
                            allDisabled
                          )
                        }
                      >
                        {allDisabled ? "Show All" : "Hide All"}
                      </OutlinedButton>
                    );
                  })()}
                  <Box onClick={() => toggleSection(section.id)} sx={{ cursor: "pointer" }}>
                    {expandedSectionIds.includes(section.id) ? (
                      <KeyboardArrowUp fontSize="small" />
                    ) : (
                      <KeyboardArrowDown fontSize="small" />
                    )}
                  </Box>
                </Box>
              </Stack>
              <Collapse in={expandedSectionIds.includes(section.id)}>
                <Stack direction="column" gap={0.5} mb={2}>
                  {section.items.map((item) => (
                    <LegendItem
                      key={item.id}
                      item={{
                        ...item,
                        title: truncate(item.title, { length: 36 }),
                        isActive: activeFilterIds.includes(item.id),
                      }}
                      handleClick={() => toggleItem(item.id)}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Stack>
            {index < sections.length - 1 && <Divider />}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default FilterComponent;
